import { useMemo } from 'react';
import { Info } from 'lucide-react';
import { computeRemainingAvailability, formatTime12Hour } from '@/utils/calendarHelpers';
import { DAY_NAMES_SHORT } from '@/utils/constants';
import type { DayData } from './types';

const MAX_SLOTS = 5;

interface UnfilledSlot {
  label: string;
  start: string;
  end: string;
}

interface UnfilledSlotsAlertProps {
  calendarDays: DayData[];
}

export function UnfilledSlotsAlert({ calendarDays }: UnfilledSlotsAlertProps) {
  const { slots, totalCount } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Flatten all unfilled windows into individual slots, sorted by date (calendarDays is already sorted)
    const all: UnfilledSlot[] = [];

    for (const day of calendarDays) {
      const dayDate = new Date(day.date);
      dayDate.setHours(0, 0, 0, 0);
      if (dayDate < today) continue;
      if (!day.isCurrentMonth) continue;

      const remaining = computeRemainingAvailability(day.availabilityWindows, day.claimedSlots);
      if (remaining.length > 0) {
        const dayLabel = DAY_NAMES_SHORT[dayDate.getDay()];
        for (const w of remaining) {
          all.push({ label: dayLabel, start: w.start, end: w.end });
        }
      }
    }

    return { slots: all.slice(0, MAX_SLOTS), totalCount: all.length };
  }, [calendarDays]);

  if (slots.length === 0) return null;

  const extraCount = totalCount - MAX_SLOTS;

  return (
    <div
      data-testid="container-unfilled-slots-alert"
      className="p-3 sm:p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl text-blue-400"
    >
      <div className="flex items-center gap-2 mb-1">
        <Info size={18} className="flex-shrink-0" />
        <span className="font-semibold text-sm">Slots need coverage</span>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 pl-[26px]">
        {slots.map((s, i) => (
          <span key={i} className="text-xs text-blue-400/80">
            {s.label} {formatTime12Hour(s.start)}-{formatTime12Hour(s.end)}
          </span>
        ))}
        {extraCount > 0 && (
          <span className="text-xs text-blue-400/50">+{extraCount} more</span>
        )}
      </div>
    </div>
  );
}
